"use client";

import { useState, useEffect, useRef, useMemo } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc.js";
import timezone from "dayjs/plugin/timezone.js";
import { KbvPluginService } from "@/services/kbvPluginService";
import { useRemoteLeagueSearch, fetchHotLeagues } from "@/hooks/useRemoteLeagueSearch";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import Loading from "@/components/Loading";
import NoData from "@/components/NoData";

dayjs.extend(utc);
dayjs.extend(timezone);
const TIME_ZONE_VN = "Asia/Ho_Chi_Minh";

const normalizeScore = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  return Number.isNaN(num) ? "-" : String(num);
};

const trimRoundEntries = (entries, maxMatches) => {
  const out = [];
  let n = 0;
  for (const [round, matches] of entries) {
    if (n >= maxMatches) break;
    const take = Math.min(matches.length, maxMatches - n);
    if (take > 0) {
      out.push([round, matches.slice(0, take)]);
      n += take;
    }
  }
  return out;
};

const fetchResultsByGroupRound = async ({ id, currentSeason , currentRound }) => {
  const CURRENT_SEASON = currentSeason;
  try {
      const round = currentRound;
      const paramFilter = `leagueId:${id},round:${round},season:${CURRENT_SEASON},status:end,`;
      const data = await KbvPluginService.fetchMatchesByGroupRound({
          search: "",
          filter: paramFilter,
          fields:
              "id,leagueId,matchTime,status,homeId,homeName,homeScore,awayId,awayName,awayScore,season,round,group,date,slug,homeYellow,homeRed,awayYellow,awayRed,homeCorner,awayCorner,homeHalfScore,awayHalfScore,homeRank,awayRank,gioVangLiveUrl",
          sort: "matchTime:desc",
          include: "homeTeam,awayTeam,odds,countOtherOdds,stats,events,insight,league",
          page: 1,
          limit: 50,
      });
      const list = data?.data?.data || [];

      const map = new Map();
      for (const match of list) {
          const r = match.round ?? round;
          if (!map.has(r)) map.set(r, []);
          map.get(r).push(match);
      }
      return map;
  } catch {
      return new Map();
  }
};



export default function LichiThiDauV2() {
  const COLLAPSED_ITEMS_COUNT = 10;
  const [hotLeagues, setHotLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  
  const [standingsData, setStandingsData] = useState(null);
  const [isStandingsLoading, setIsStandingsLoading] = useState(false);

  const [fixtureData, setFixtureData] = useState(null);
  const [isFixtureLoading, setIsFixtureLoading] = useState(false);
  
  const dropdownRef = useRef(null);
  const debouncedQuery = useDebouncedValue(searchQuery, 350).trim();

  // Search logic reuse
  const { results: remoteLeagues, isLoading: isSearchLoading } = useRemoteLeagueSearch({
    enabled: isDropdownOpen,
    keyword: debouncedQuery,
  });

  // Initial load: Hot Leagues
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await fetchHotLeagues();
      if (cancelled) return;
      setHotLeagues(list || []);
      if (list && list.length > 0 && !selectedLeague) {
        const defaultLeague = list.find((league) => Number(league?.id) === 1639) || list[0];
        setSelectedLeague(defaultLeague);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch standings when league changes
  useEffect(() => {
    if (!selectedLeague?.id) return;
    
    let cancelled = false;
    setIsFixtureLoading(true);
    (async () => {
      try {
        const data = await fetchResultsByGroupRound(selectedLeague);
        if (cancelled) return;
        setFixtureData(data); 
      } catch (error) {
        console.error("Fetch fixture error:", error);
        setFixtureData(null);
      } finally {

         if (!cancelled) setIsFixtureLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [selectedLeague?.id]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const displayLeagues = useMemo(() => {
    if (debouncedQuery) return remoteLeagues;
    return hotLeagues;
  }, [debouncedQuery, remoteLeagues, hotLeagues]);

  const fixtureRoundEntries = useMemo(() => {
    if (!fixtureData || !(fixtureData instanceof Map) || fixtureData.size === 0) return [];
    return [...fixtureData.entries()].sort((a, b) => Number(a[0]) - Number(b[0]));
  }, [fixtureData]);

  const fixtureMatchTotal = useMemo(
    () => fixtureRoundEntries.reduce((sum, [, matches]) => sum + matches.length, 0),
    [fixtureRoundEntries]
  );

  const handleSelect = (league) => {
    setSelectedLeague(league);
    setIsDropdownOpen(false);
    setSearchQuery("");
    setIsExpanded(false);
  };

  const renderTable = () => {
    if (isFixtureLoading) {
      return (
        <div className="flex justify-center py-10">
          <Loading />
        </div>
      );
    }

    if (!fixtureData || !(fixtureData instanceof Map)) return null;
    if (fixtureData.size === 0) {
      return (
        <NoData>
          <span className="text-xs text-gray-500">Không có trận đấu</span>
        </NoData>
      );
    }

    const entriesToRender = isExpanded
      ? fixtureRoundEntries
      : trimRoundEntries(fixtureRoundEntries, COLLAPSED_ITEMS_COUNT);
    const showToggleButton = fixtureMatchTotal > COLLAPSED_ITEMS_COUNT;

    return (
      <>
        <div className="max-h-[min(60vh,420px)] overflow-y-auto overflow-x-hidden rounded-lg border border-gray-200 league-filter-scrollbar">
          {entriesToRender.map(([round, matches]) => (
            <div key={String(round)} className="border-b border-gray-100 last:border-b-0">
              <div className="sticky top-0 z-[1] bg-gradient-to-b from-gray-100 to-gray-50 px-2.5 py-2 text-[13px] font-extrabold tracking-wide text-gray-700 sm:px-3 sm:text-[14px]">
                Vòng {round}
              </div>
              <div>
                {matches.map((match) => {
                  const homeLogo = match?.homeTeam?.logo || match?.homeLogo;
                  const awayLogo = match?.awayTeam?.logo || match?.awayLogo;
                  const homeName = match?.homeTeam?.name || match?.homeName || "—";
                  const awayName = match?.awayTeam?.name || match?.awayName || "—";
                  const statusNum = Number(match?.status ?? 0);
                  const isNotStarted = statusNum === 0;
                  const homeScore = isNotStarted ? "-" : normalizeScore(match?.homeScore);
                  const awayScore = isNotStarted ? "-" : normalizeScore(match?.awayScore);
                  const when =
                    match?.matchTime != null && match?.matchTime !== ""
                      ? dayjs.unix(Number(match.matchTime)).tz(TIME_ZONE_VN).format("DD/MM HH:mm")
                      : match?.date || "";

                  return (
                    <div
                      key={match.id ?? `${round}-${homeName}-${awayName}-${match.matchTime}`}
                      className="px-2 py-2.5 sm:px-3 sm:py-3.5"
                    >
                      {when ? (
                        <div className="mb-2 text-[13px] font-semibold leading-tight text-gray-600 sm:mb-2 sm:text-[14px]">
                          {when}
                        </div>
                      ) : null}
                      <div className="grid w-full grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-x-2 gap-y-1 sm:gap-x-3">
                        <div className="flex min-w-0 items-center justify-end self-center">
                          <span
                            className="truncate text-right text-[14px] font-bold leading-snug text-stroke-dark sm:text-[15px] md:text-[16px]"
                            title={homeName}
                          >
                            {homeName}
                          </span>
                        </div>
                        <div className="flex items-center justify-center gap-1.5 self-center sm:gap-2">
                          {homeLogo ? (
                            <img
                              src={homeLogo}
                              alt=""
                              width={36}
                              height={36}
                              className="h-8 w-8 shrink-0 object-contain sm:h-9 sm:w-9 md:h-10 md:w-10"
                              loading="lazy"
                            />
                          ) : (
                            <span className="h-8 w-8 shrink-0 rounded-full bg-gray-100 sm:h-9 sm:w-9 md:h-10 md:w-10" aria-hidden />
                          )}
                          <div className="flex items-center justify-center gap-0.5 text-[17px] font-extrabold tabular-nums leading-none text-red-600 sm:gap-1 sm:text-[18px] md:text-[19px]">
                            <span>{homeScore}</span>
                            <span className="text-[15px] font-bold text-gray-400 sm:text-base md:text-lg">:</span>
                            <span>{awayScore}</span>
                          </div>
                          {awayLogo ? (
                            <img
                              src={awayLogo}
                              alt=""
                              width={36}
                              height={36}
                              className="h-8 w-8 shrink-0 object-contain sm:h-9 sm:w-9 md:h-10 md:w-10"
                              loading="lazy"
                            />
                          ) : (
                            <span className="h-8 w-8 shrink-0 rounded-full bg-gray-100 sm:h-9 sm:w-9 md:h-10 md:w-10" aria-hidden />
                          )}
                        </div>
                        <div className="flex min-w-0 items-center justify-start self-center">
                          <span
                            className="truncate text-left text-[14px] font-bold leading-snug text-stroke-dark sm:text-[15px] md:text-[16px]"
                            title={awayName}
                          >
                            {awayName}
                          </span>
                        </div>
                      </div>
                      <div
                        className="-mx-2 mt-2.5 h-px bg-gray-200 sm:-mx-3 sm:mt-3"
                        aria-hidden
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        {showToggleButton && (
          <button
            type="button"
            onClick={() => setIsExpanded((prev) => !prev)}
            className="mt-2 w-full rounded-lg border border-gray-200 py-2 text-[13px] font-semibold text-stroke-dark transition-colors hover:bg-gray-50 sm:text-[14px]"
          >
            {isExpanded ? "Thu gọn" : "Mở rộng"}
          </button>
        )}
      </>
    );
  };

  return (
    <aside className="min-h-87.5 w-full self-start overflow-hidden rounded border border-gray-100 bg-white shadow-sm" aria-label="Ranking per tournament">
      <div className="flex flex-col gap-2 p-2 sm:p-2.5">
        <div className="flex items-center gap-2">
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#fef3c7]">
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 text-[#d97706]" fill="currentColor" aria-hidden="true">
                <path d="M8.5 3a1 1 0 0 0-1 1v1H5.75A1.75 1.75 0 0 0 4 6.75V8a4.5 4.5 0 0 0 4 4.472A4.99 4.99 0 0 0 11 14.91V17H8.5a1 1 0 1 0 0 2h7a1 1 0 1 0 0-2H13v-2.09a4.99 4.99 0 0 0 3-2.438A4.5 4.5 0 0 0 20 8V6.75A1.75 1.75 0 0 0 18.25 5H16.5V4a1 1 0 0 0-1-1h-7ZM6 8V7h1.5v2.997A2.5 2.5 0 0 1 6 8Zm10.5-.003V7H18v1a2.5 2.5 0 0 1-1.5 1.997V7.997Z" />
              </svg>
            </span>
            <div className="text-[16px] font-bold text-stroke-dark sm:text-[17px]">Lịch Thi Đấu</div>
        </div>

        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex h-8 w-full items-center justify-between rounded-full border border-gray-200 bg-white px-3 text-[13px] transition-all hover:border-primary sm:px-4 sm:text-[14px]"
            >
                <div className="flex items-center gap-2 overflow-hidden">
                    {selectedLeague?.logo && (
                        <img 
                            src={selectedLeague.logo} 
                            alt="" 
                            width={14} 
                            height={14} 
                            className="object-contain" 
                        />
                    )}
                    <span className="truncate font-medium text-stroke-dark">
                        {selectedLeague?.nameVN || selectedLeague?.name || "Chọn giải đấu..."}
                    </span>
                </div>
                <img 
                    src="/arrow-up.svg" 
                    alt="" 
                    width={16}
                    height={16}
                    className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-0' : 'rotate-180'}`}
                />
            </button>

            {isDropdownOpen && (
                <div className="absolute left-0 right-0 top-[40px] z-[100] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.12)]">
                    <div className="border-b border-gray-100 bg-gradient-to-b from-gray-50 to-white p-2.5">
                        <div className="relative">
                            <input
                                autoFocus
                                type="text"
                                placeholder="Tìm kiếm giải đấu..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="h-9 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 text-[13px] text-gray-700 outline-none transition-all placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 sm:text-[14px]"
                            />
                            <img 
                                src="/search.svg" 
                                alt="" 
                                width={14}
                                height={14}
                                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 opacity-45" 
                            />
                        </div>
                    </div>
                    <div className="max-h-[50vh] overflow-y-auto py-1 league-filter-scrollbar sm:max-h-[240px]">
                        {isSearchLoading ? (
                            <div className="p-4 flex justify-center"><Loading /></div>
                        ) : displayLeagues.map((league) => (
                            <button
                                key={league.id}
                                onClick={() => handleSelect(league)}
                                className={`flex w-full items-center gap-2.5 px-3 py-2.5 text-left transition-colors sm:px-3.5 ${selectedLeague?.id === league.id ? "bg-blue-50/70 text-blue-700" : "text-gray-700 hover:bg-gray-50"}`}
                            >
                                {league.logo && (
                                    <img 
                                        src={league.logo} 
                                        alt="" 
                                        width={16} 
                                        height={16} 
                                        className="object-contain" 
                                    />
                                )}
                                <span className={`truncate text-[13px] sm:text-[14px] ${selectedLeague?.id === league.id ? "font-semibold" : "font-medium"}`}>{league.nameVN || league.name}</span>
                                {selectedLeague?.id === league.id && (
                                    <img 
                                        src="/icons/check-icon.svg" 
                                        alt="" 
                                        width={14}
                                        height={14}
                                        className="ml-auto opacity-90" 
                                    />
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>

        <div>
            {renderTable()}
        </div>

      </div>
    </aside>
  );
}
